import { useQuery } from '@tanstack/react-query';
import { STALE_TIME_LIST_MS } from '../../constants/reactQueryStaleTime.ts';
import type { ICareHomeListResponse } from '../../interfaces';
import type { IResidentListResponse } from '../../interfaces/Resident/IResidentListResponse.ts';
import type { IStation } from '../../interfaces/Station/IStation.ts';
import CareHomeService from '../../services/CareHomeService.ts';
import ResidentService from '../../services/ResidentService.ts';
import StationService from '../../services/StationService.ts';
import { ResidentState } from '../../constants/ResidentState.ts';
import { UserRole } from '../../constants/UserRoles.ts';

export type ResidentsListQueryInput = {
  search: string;
  userRole?: string;
  organisationId?: string;
  userCareHomeId?: string;
};

export const getResidentsListQueryKey = (input: ResidentsListQueryInput) =>
  ['residents', input.userRole, input.organisationId, input.userCareHomeId, input.search] as const;

export function useResidentsListQuery(input: ResidentsListQueryInput, enabled: boolean) {
  return useQuery<IResidentListResponse>({
    queryKey: getResidentsListQueryKey(input),
    enabled: enabled && !!input.userRole,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: async () => {
      const searchParams = new URLSearchParams(input.search);
      const params: Record<string, any> = {
        ...Object.fromEntries(searchParams),
      };

      const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
      const perPageFromUrl = parseInt(searchParams.get('perPage') || '10', 10);

      params.page = pageFromUrl;
      params.per_page = perPageFromUrl;

      // Only use carehome_id from URL if it exists, otherwise use user's carehome_id
      params.carehome_id = searchParams.get('carehome_id') || input.userCareHomeId;

      if (input.userRole === UserRole.ORGANISATION_MANAGER && input.organisationId) {
        params.organisation_id = input.organisationId;
      }

      // Send all status values when rstate is not selected
      if (!searchParams.has('rstate')) {
        params.rstate = `${ResidentState.ENABLED},${ResidentState.DISABLED},${ResidentState.IN_HOSPITAL},${ResidentState.OUT_OF_CARE}`;
      }

      return ResidentService.getResidentList(params);
    },
  });
}

export const getStationsForResidentsQueryKey = () => ['stationsForResidentsFilters'] as const;

export function useStationsForResidentsQuery() {
  return useQuery<IStation[]>({
    queryKey: getStationsForResidentsQueryKey(),
    staleTime: STALE_TIME_LIST_MS,
    queryFn: async () => {
      const stationsResponse = await StationService.getListStations();
      return stationsResponse.items ?? [];
    },
  });
}

export const getCareHomesForResidentsQueryKey = (organisationId?: string) =>
  ['careHomesForResidentsFilters', organisationId] as const;

export function useCareHomesForResidentsQuery(organisationId?: string, enabled = true) {
  return useQuery<ICareHomeListResponse>({
    queryKey: getCareHomesForResidentsQueryKey(organisationId),
    enabled: enabled && !!organisationId,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () =>
      CareHomeService.getCareHomes({
        organisation_id: organisationId!,
      }),
  });
}

